import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, HardHat, MapPin, Clock, Users, Palmtree, Plus, Lock, Pencil, Trash2, X } from 'lucide-react';
import { DailyJobEditor } from './DailyJobEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const DRAG_MIME = 'application/x-365-crew';

export function JobCard({ job, canEdit, onEdit, onDelete, onDropCrew, onRemoveCrew }) {
  const [over, setOver] = useState(false);
  const people = job.crew_matched || [];
  const ops = job.source === 'ops';
  const dead = job.superseded_by_sheet;
  const key = job.id || job.block;
  const droppable = canEdit && ops && !dead && !!onDropCrew;
  const chipDrag = (p) => (droppable ? {
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ id: p.employee_id, name: p.sheet_name, from_job_id: job.id, from_wo: job.work_order || '' })); e.dataTransfer.effectAllowed = 'move'; },
  } : {});
  const dragProps = canEdit && onDropCrew ? {
    onDragOver: (e) => { if (e.dataTransfer.types.includes(DRAG_MIME)) { e.preventDefault(); e.dataTransfer.dropEffect = droppable ? 'move' : 'none'; setOver(true); } },
    onDragLeave: () => setOver(false),
    onDrop: (e) => {
      setOver(false);
      const raw = e.dataTransfer.getData(DRAG_MIME); if (!raw) return;
      e.preventDefault();
      const who = JSON.parse(raw);
      if (!droppable) { toast.error(dead ? 'This Ops block is parked — the sheet has this WO today' : `${who.name} can't be dropped on a sheet block — add him on the Google sheet, or on an Ops block`); return; }
      onDropCrew(who, job);
    },
  } : {};
  return (
    <div {...dragProps} className={`bg-white border rounded-xl p-4 relative transition-shadow ${dead ? 'opacity-60 border-dashed border-slate-300' : ops ? 'border-emerald-300 shadow-[inset_3px_0_0_#10b981]' : 'border-slate-200'} ${over ? (droppable ? 'ring-2 ring-emerald-500 bg-emerald-50/40' : 'ring-2 ring-red-300 cursor-not-allowed') : ''}`} data-testid={`mp-job-${key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-slate-900 font-bold leading-snug">{job.customer || <span className="text-slate-500 italic">No customer</span>}</p>
          {job.description && <p className="text-xs text-slate-700 mt-0.5">{job.description}</p>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {job.work_order && <span className="font-mono text-blue-700 font-semibold text-sm">{job.work_order}</span>}
          {ops ? (
            canEdit && !dead && (
              <>
                <button onClick={() => onEdit(job)} className="p-1 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Edit (Ops entry)" data-testid={`mp-job-edit-${key}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => onDelete(job)} className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove" data-testid={`mp-job-delete-${key}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )
          ) : (
            <span className="p-1 text-slate-400" title="From the Google dispatch sheet — change it there (the sheet wins for now)" data-testid={`mp-job-lock-${key}`}><Lock className="w-3.5 h-3.5" /></span>
          )}
        </div>
      </div>
      {dead && <p className="text-[11px] text-amber-800 mt-1 font-semibold">Sheet has this WO today — the sheet's block is used, this Ops entry is parked.</p>}
      <div className="mt-2 text-xs text-slate-500 space-y-0.5">
        {job.place && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {job.place}</p>}
        {job.start && <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Start {job.start}</p>}
        {(job.contact || job.tools) && <p>{[job.contact && `Contact: ${job.contact}`, job.tools && `Tools: ${job.tools}`].filter(Boolean).join(' · ')}</p>}
        {(job.truck || job.trailer || job.welder || job.forklift) && <p>{[job.truck && `Truck ${job.truck}`, job.trailer && `Trailer ${job.trailer}`, job.welder && `Welder ${job.welder}`, job.forklift && `Forklift ${job.forklift}`].filter(Boolean).join(' · ')}</p>}
        {job.note && <p className="text-amber-800 italic">{job.note}</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`mp-job-crew-${key}`}>
        {people.length === 0 && <span className="text-xs text-slate-500 italic">No crew assigned yet</span>}
        {people.map((p, i) => (
          <span key={i} {...chipDrag(p)} title={`${p.employee_id ? `Matched: ${p.employee_name}` : 'No 365Ops user matches this name — they will NOT see it on their dashboard'}${droppable ? ' · drag to another block, × to take off' : ''}`}
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${p.is_foreman ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-100 border-slate-300 text-slate-800'} ${droppable ? 'cursor-grab active:cursor-grabbing' : ''}`}
            data-testid={`mp-job-crew-chip-${key}-${p.employee_id || i}`}>
            {p.is_foreman && <HardHat className="w-3 h-3" />}{p.sheet_name}{!p.employee_id && <AlertTriangle className="w-3 h-3" />}
            {droppable && onRemoveCrew && (
              <button type="button" onClick={(e) => { e.stopPropagation(); onRemoveCrew(p, job); }} className="ml-0.5 -mr-1 p-0.5 rounded-full hover:bg-red-100 hover:text-red-700" title={`Take ${p.sheet_name} off this job`} data-testid={`mp-job-crew-remove-${key}-${p.employee_id || i}`}><X className="w-3 h-3" /></button>
            )}
          </span>
        ))}
      </div>
      {ops && <span className="absolute -top-2 left-3 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded px-1.5 py-0.5">Ops</span>}
      {over && droppable && <span className="absolute inset-x-0 bottom-1 text-center text-[11px] font-bold text-emerald-700">Drop to put him on this job</span>}
    </div>
  );
}

const ROSTER_TONE = {
  working: 'bg-emerald-500 border-emerald-600 text-white',
  available: 'bg-white border-slate-300 text-slate-800',
  off: 'bg-amber-400 border-amber-500 text-amber-950',
};

/** Every field guy for the day — green working · white available · amber booked off. Drag onto a job block to assign, or click for a menu. */
function CrewRoster({ roster, canEdit, targets = [], onAssign, onAddJob }) {
  const [pick, setPick] = useState(null); // roster id with the "Put on…" menu open
  const counts = roster.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  const order = { working: 0, available: 1, off: 2 };
  const sorted = [...roster].sort((a, b) => order[a.status] - order[b.status] || a.name.localeCompare(b.name));
  useEffect(() => {
    if (!pick) return undefined;
    const close = () => setPick(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [pick]);
  const who = (r) => ({ id: r.id, name: r.name, from_job_id: r.job?.source === 'ops' ? r.job.job_id : null, from_wo: r.job?.work_order || '' });
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4" data-testid="mp-roster">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-slate-700 uppercase flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Crew today</p>
        <p className="text-[11px] text-slate-600 flex items-center gap-3" data-testid="mp-roster-counts">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />{counts.working || 0} working</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-white border border-slate-400" />{counts.available || 0} available</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" />{counts.off || 0} booked off</span>
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map(r => {
          const clickable = canEdit && r.status !== 'off';
          const open = pick === r.id;
          const options = targets.filter(j => !(r.job?.source === 'ops' && r.job.job_id === j.id));
          return (
            <span key={r.id} className="relative inline-block">
              <span draggable={clickable}
                onDragStart={(e) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify(who(r))); e.dataTransfer.effectAllowed = 'move'; }}
                onClick={(e) => { if (!clickable) return; e.stopPropagation(); setPick(open ? null : r.id); }}
                title={r.status === 'working' ? `On ${r.job?.work_order || 'a job'}${r.job?.customer ? ` — ${r.job.customer}` : ''}${canEdit ? ' · drag or click to move him' : ''}` : r.status === 'off' ? `Booked off — ${r.reason}` : `Available${canEdit ? ' — drag onto a job block, or click' : ''}`}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border select-none ${ROSTER_TONE[r.status]} ${clickable ? 'cursor-pointer hover:shadow active:cursor-grabbing' : ''} ${open ? 'ring-2 ring-sky-400' : ''}`}
                data-testid={`mp-roster-${r.id}`} data-status={r.status}>
                {r.name}
                {r.status === 'working' && r.job?.work_order && <span className="font-mono font-normal opacity-80 text-[10px]">{r.job.work_order}</span>}
                {r.status === 'off' && r.reason && <span className="font-normal opacity-80 text-[10px]">{r.reason}</span>}
              </span>
              {open && (
                <div onClick={(e) => e.stopPropagation()} className="absolute left-0 top-full mt-1 z-40 w-72 bg-white border border-slate-200 rounded-xl shadow-2xl py-1.5 text-left" data-testid={`mp-roster-menu-${r.id}`}>
                  <p className="px-3 pb-1 text-[11px] font-bold text-slate-500 uppercase tracking-wide">Put {r.name.split(' ')[0]} on…</p>
                  {options.length === 0 && <p className="px-3 py-1.5 text-xs text-slate-500">No Ops job blocks on this day yet.</p>}
                  {options.map(j => (
                    <button key={j.id} type="button" onClick={() => { setPick(null); onAssign(who(r), j); }} data-testid={`mp-roster-menu-${r.id}-${j.id}`}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-50 flex items-center gap-2">
                      <span className="font-mono text-blue-700 font-semibold">{j.work_order || '—'}</span>
                      <span className="truncate text-slate-800">{j.customer}</span>
                      <span className="ml-auto text-[10px] text-slate-500 shrink-0">{(j.crew_matched || []).length} on</span>
                    </button>
                  ))}
                  {onAddJob && <button type="button" onClick={() => { setPick(null); onAddJob(r); }} className="w-full text-left px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-50 border-t border-slate-100 mt-1 inline-flex items-center gap-1" data-testid={`mp-roster-menu-${r.id}-new`}><Plus className="w-3.5 h-3.5" /> New job block with {r.name.split(' ')[0]}</button>}
                </div>
              )}
            </span>
          );
        })}
        {roster.length === 0 && <span className="text-xs text-slate-500">No field crew set up yet.</span>}
      </div>
      {canEdit && <p className="text-[11px] text-slate-500 mt-2">Drag a guy onto a job block — or click his name and pick the job. Green guys move between Ops blocks; sheet blocks are locked while the sheet wins.</p>}
    </div>
  );
}

/** One day tab's content — the dispatch-sheet day: job blocks, off list, booked-off, projections to fill. */
export function DayView({ date, canEdit, onChanged, compact = false }) {
  const [day, setDay] = useState(null);
  const [picklists, setPicklists] = useState(null);
  const [editor, setEditor] = useState(null); // { job } | { prefill }
  const [vac, setVac] = useState({ employee: '', reason: '' });

  const [loadError, setLoadError] = useState(false);
  const loadDay = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try { const r = await axios.get(`${API}/dailies/day/${date}`, auth()); setDay(r.data); setLoadError(false); return; }
      catch (e) { if (attempt === 2 || (e.response?.status && e.response.status < 429)) { setDay(null); setLoadError(true); return; } await new Promise(res => setTimeout(res, 1500 * (attempt + 1))); }
    }
  }, [date]);
  useEffect(() => { loadDay(); }, [loadDay]);
  useEffect(() => { axios.get(`${API}/dailies/picklists`, auth()).then(r => setPicklists(r.data)).catch(() => {}); }, []);
  const refresh = () => { loadDay(); onChanged?.(); };

  const deleteJob = async (job) => {
    if (!window.confirm(`Remove ${job.work_order || job.customer} from ${fmtDay(date)}?`)) return;
    try { await axios.delete(`${API}/dailies/day/${date}/jobs/${job.id}`, auth()); toast.success('Removed'); refresh(); } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
  };
  const addVacation = async (e) => {
    e.preventDefault();
    if (!vac.employee.trim()) return;
    try { await axios.post(`${API}/dailies/day/${date}/vacations`, vac, auth()); setVac({ employee: '', reason: '' }); toast.success(`${vac.employee} booked off`); refresh(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not add'); }
  };
  const removeVacation = async (v) => {
    try { await axios.delete(`${API}/dailies/day/${date}/vacations/${v.id}`, auth()); refresh(); } catch (err) { toast.error(err.response?.data?.detail || 'Only Ops entries can be removed here'); }
  };
  const dropCrew = async (who, job) => {
    try {
      await axios.post(`${API}/dailies/day/${date}/move-crew`, { name: who.name, to_job_id: job.id || job.block, from_job_id: who.from_job_id || null }, auth());
      toast.success(who.from_job_id ? `${who.name} moved from ${who.from_wo || 'his job'} to ${job.work_order || job.customer}` : `${who.name} put on ${job.work_order || job.customer}${who.from_wo ? ` (still on sheet job ${who.from_wo} too — fix the sheet)` : ''}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not move him'); }
  };
  const removeCrew = async (p, job) => {
    try {
      await axios.post(`${API}/dailies/day/${date}/remove-crew`, { name: p.sheet_name, to_job_id: job.id }, auth());
      toast.success(`${p.sheet_name} taken off ${job.work_order || job.customer}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not take him off'); }
  };

  const jobs = (day?.jobs || []).filter(j => j.kind !== 'off');
  const offBlocks = (day?.jobs || []).filter(j => j.kind === 'off');
  const projections = day?.projections || [];
  const people = jobs.filter(j => !j.superseded_by_sheet).reduce((n, j) => n + (j.crew_matched || []).length, 0);
  const opsTargets = jobs.filter(j => j.source === 'ops' && !j.superseded_by_sheet);

  return (
    <div data-testid="dailies-day-view">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className={`text-slate-900 font-bold ${compact ? 'text-base' : 'text-lg'}`} data-testid="mp-selected-day">{new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          {day?.synced_at && <span className="ml-2 text-xs font-normal text-slate-500">sheet read {new Date(day.synced_at).toLocaleTimeString()}</span>}
          {jobs.length > 0 && <span className="ml-2 text-xs font-normal text-slate-500 inline-flex items-center gap-1" data-testid="mp-day-people"><Users className="w-3 h-3" /> {people} people</span>}
        </h2>
        {canEdit && (
          <button onClick={() => setEditor({})} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-sky-700 hover:bg-sky-600 rounded-lg px-3 py-2" data-testid="mp-add-job">
            <Plus className="w-4 h-4" /> Add job block
          </button>
        )}
      </div>

      {projections.length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="mp-projections">
          <p className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1.5">Projected for this day — names to fill in</p>
          <div className="flex flex-wrap gap-2">
            {projections.map(p => (
              <div key={p.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${p.filled ? 'bg-white border-emerald-300' : 'bg-white border-amber-300'}`} data-testid={`mp-projection-${p.id}`}>
                <span className="inline-flex items-center justify-center rounded-full bg-amber-600 text-white font-bold text-xs px-1.5 h-5">{p.planned_crew}?</span>
                <span className="font-mono font-semibold text-slate-900">{p.work_order}</span>
                <span className="text-slate-600 truncate max-w-[220px]">{p.title || p.customer}</span>
                {p.from_sheet && <span className="text-[10px] font-bold uppercase text-amber-700 border border-amber-300 rounded px-1" title="Number typed on the sheet's Monthly View">sheet</span>}
                {p.filled ? <span className="text-[11px] font-bold text-emerald-700">names in</span> : canEdit && (
                  <button onClick={() => setEditor({ prefill: { work_order: p.work_order, customer: p.customer, place: p.place, start: p.start_time, description: p.title, planned_crew: p.planned_crew } })}
                    className="text-xs font-bold text-amber-800 hover:underline" data-testid={`mp-projection-fill-${p.id}`}>Fill in →</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loadError ? (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="mp-day-error">Couldn't load this day. <button onClick={loadDay} className="font-bold underline">Try again</button></p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="mp-day-empty">Nothing on this day yet{canEdit ? ' — add a job block, or it will fill in when the sheet is read.' : '.'}</p>
      ) : (
        <div className={`grid gap-3 ${compact ? 'md:grid-cols-2 2xl:grid-cols-3' : 'md:grid-cols-2 xl:grid-cols-3'}`}>{jobs.map(j => <JobCard key={j.id || j.block} job={j} canEdit={canEdit} onEdit={(job) => setEditor({ job })} onDelete={deleteJob} onDropCrew={dropCrew} onRemoveCrew={removeCrew} />)}</div>
      )}
      {offBlocks.length > 0 && (
        <div className="mt-4 bg-white border border-dashed border-slate-300 rounded-xl p-4" data-testid="mp-off-list">
          <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Off today (from the sheet)</p>
          {offBlocks.map(j => (
            <p key={j.id || j.block} className="text-sm text-slate-700"><span className="text-slate-500">{j.customer}:</span> {(j.crew_matched || []).map(p => p.sheet_name).join(', ') || '—'}</p>
          ))}
        </div>
      )}
      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4" data-testid="mp-vacations">
        <p className="text-xs font-semibold text-slate-700 uppercase flex items-center gap-1.5 mb-2"><Palmtree className="w-3.5 h-3.5" /> Vacation / personal days booked</p>
        <div className="flex flex-wrap gap-1.5">
          {(day?.vacations || []).length === 0 && <span className="text-xs text-slate-500">Nobody booked off.</span>}
          {(day?.vacations || []).map((v, i) => (
            <span key={v.id || i} className={`text-xs border rounded-full pl-2 pr-1 py-1 text-slate-800 inline-flex items-center gap-1 ${v.source === 'ops' ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-100 border-slate-300'}`} data-testid={`mp-vacation-${v.id || i}`}>
              {v.employee}{v.reason ? <span className="text-slate-500"> — {v.reason}</span> : null}
              {v.source === 'ops' && canEdit ? <button onClick={() => removeVacation(v)} className="p-0.5 rounded-full hover:bg-slate-300" data-testid={`mp-vacation-remove-${v.id}`}><Trash2 className="w-3 h-3" /></button> : <span className="w-1" />}
            </span>
          ))}
        </div>
        {canEdit && (
          <form onSubmit={addVacation} className="mt-3 flex flex-wrap gap-2">
            <input list="mp-crew-names" value={vac.employee} onChange={e => setVac(v => ({ ...v, employee: e.target.value }))} placeholder="Who's off…" className="h-8 rounded-lg border border-slate-300 px-2.5 text-sm w-48" data-testid="mp-vacation-name" />
            <datalist id="mp-crew-names">{(picklists?.crew || []).map(p => <option key={p.id} value={p.name} />)}</datalist>
            <input value={vac.reason} onChange={e => setVac(v => ({ ...v, reason: e.target.value }))} placeholder="Reason (Sick, Vacation, Block 2…)" className="h-8 rounded-lg border border-slate-300 px-2.5 text-sm w-60" data-testid="mp-vacation-reason" />
            <button type="submit" className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold" data-testid="mp-vacation-add">Book off</button>
          </form>
        )}
      </div>

      {day?.roster && <CrewRoster roster={day.roster} canEdit={canEdit} targets={opsTargets} onAssign={dropCrew} onAddJob={canEdit ? (r) => setEditor({ prefill: { crew: [r.name] } }) : undefined} />}

      {editor && <DailyJobEditor date={date} job={editor.job} prefill={editor.prefill} picklists={picklists} onClose={() => setEditor(null)} onSaved={refresh} />}
    </div>
  );
}
